import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { PasswordInput } from "@/components/PasswordInput";

export default function AdminSettings() {
  const [form, setForm] = useState({ delivery_fee: 85, free_delivery_over: 750, collection_note: "" });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => {
      setForm({
        delivery_fee: r.data.delivery_fee ?? 85,
        free_delivery_over: r.data.free_delivery_over ?? 750,
        collection_note: r.data.collection_note || "",
      });
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/settings", {
        delivery_fee: Number(form.delivery_fee),
        free_delivery_over: Number(form.free_delivery_over),
        collection_note: form.collection_note,
      });
      toast.success("Store settings saved");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  if (!loaded) return <div className="h-40 bg-white rounded-2xl border border-slate-100 animate-pulse" />;

  return (
    <>
      <form onSubmit={save} className="bg-white rounded-3xl border border-slate-200/80 p-8 max-w-xl" data-testid="admin-settings">
      <h3 className="font-display font-bold text-navy text-lg mb-6">Delivery & collection</h3>
      <div className="space-y-4">
        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Delivery fee (R)</span>
          <input type="number" step="0.01" min="0" value={form.delivery_fee} onChange={(e) => setForm({ ...form, delivery_fee: e.target.value })} className="mt-1 w-full h-11 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:ring-2 focus:ring-gold" data-testid="setting-delivery-fee" />
        </label>
        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Free delivery over (R)</span>
          <input type="number" step="0.01" min="0" value={form.free_delivery_over} onChange={(e) => setForm({ ...form, free_delivery_over: e.target.value })} className="mt-1 w-full h-11 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:ring-2 focus:ring-gold" data-testid="setting-free-over" />
        </label>
        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Collection instructions</span>
          <textarea rows={3} value={form.collection_note} onChange={(e) => setForm({ ...form, collection_note: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gold" data-testid="setting-collection-note" />
        </label>
      </div>
      <button className="mt-6 h-11 px-8 rounded-full bg-gold hover:bg-gold-hover text-white font-bold text-sm transition-colors" data-testid="settings-save">
        Save settings
      </button>
      </form>
      <ChangePasswordCard />
      <EmployeeAccountsCard />
    </>
  );
}

function EmployeeAccountsCard() {
  const [employees, setEmployees] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "staff" });
  const [busy, setBusy] = useState(false);
  const [me, setMe] = useState(null);

  const inputCls = "w-full h-11 rounded-xl border border-slate-200 px-4 text-sm outline-none focus:ring-2 focus:ring-gold";

  const load = () =>
    api.get("/admin/users")
      .then((r) => setEmployees(r.data.users.filter((u) => u.role === "admin" || u.role === "staff")))
      .catch(() => setEmployees([]));

  useEffect(() => {
    load();
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
  }, []);

  const removeUser = async (u) => {
    if (!window.confirm(`Remove ${u.name} (${u.email}) from the admin console? Their access stops immediately.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`${u.email} removed from the console`);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/users", { ...form });
      toast.success(`${form.role === "admin" ? "Admin" : "Staff"} account created for ${form.email} — they sign in at /admin-login`);
      setCreateOpen(false);
      setForm({ name: "", email: "", password: "", role: "staff" });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-3xl border border-slate-200/80 p-8 max-w-xl mt-6" data-testid="employee-accounts-card">
      <div className="flex items-start justify-between gap-3 mb-1">
        <h3 className="font-display font-bold text-navy text-lg">Employee accounts</h3>
        <button
          onClick={() => setCreateOpen(true)}
          className="h-9 px-4 rounded-full bg-gold hover:bg-gold-hover text-white text-xs font-bold transition-colors"
          data-testid="create-user-btn"
        >
          + Create employee
        </button>
      </div>
      <p className="text-xs text-slate-400 mb-5">
        Only admins can create employee accounts. Employees sign in to this console at{" "}
        <span className="font-mono font-semibold text-navy">/admin-login</span> — every action they take is recorded in the Activity log.
      </p>
      {!employees ? (
        <div className="h-16 bg-slate-50 rounded-xl animate-pulse" />
      ) : (
        <ul className="divide-y divide-slate-100" data-testid="employee-list">
          {employees.map((u) => (
            <li key={u.id} className="py-3 flex items-center justify-between" data-testid={`employee-${u.email}`}>
              <div>
                <p className="text-sm font-semibold text-navy">{u.name}</p>
                <p className="text-xs text-slate-400">{u.email}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${u.role === "admin" ? "bg-navy text-gold" : "bg-slate-100 text-slate-500"}`}>
                  {u.role === "admin" ? "Admin" : "Staff"}
                </span>
                {me && me.id !== u.id && (
                  <button onClick={() => removeUser(u)} className="p-1.5 text-slate-300 hover:text-rose-500 transition-colors" title={`Remove ${u.email}`} data-testid={`remove-user-${u.email}`} aria-label="Remove account">
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {createOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-navy-dark/60 backdrop-blur-sm" onClick={() => setCreateOpen(false)} />
          <form onSubmit={createUser} className="relative bg-white rounded-3xl p-8 w-full max-w-md" data-testid="create-user-modal">
            <h3 className="font-display font-bold text-navy text-lg mb-1">Create employee account</h3>
            <p className="text-xs text-slate-400 mb-6">
              The employee gets full access to this admin console and signs in at{" "}
              <span className="font-mono font-semibold text-navy">/admin-login</span> with their email and this password.
            </p>
            <div className="space-y-4">
              <label className="block">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Employee full name</span>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`${inputCls} mt-1`} placeholder="e.g. Karabo Mokoena" data-testid="create-user-name" />
              </label>
              <label className="block">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Work email</span>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={`${inputCls} mt-1`} placeholder="name@btmedu.co.za" data-testid="create-user-email" />
              </label>
              <label className="block">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Temporary password (min 6 characters)</span>
                <PasswordInput required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={`${inputCls} mt-1`} data-testid="create-user-password" />
              </label>
              <label className="block">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Access level</span>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={`${inputCls} mt-1 bg-white`} data-testid="create-user-role">
                  <option value="staff">Staff — manage orders & stock, NO refunds or manual delivery overrides</option>
                  <option value="admin">Admin — full access incl. refunds, overrides & user creation</option>
                </select>
              </label>
              <p className="text-[11px] text-slate-400">They can change this password anytime in Settings → Change your password. Staff confirm deliveries with the customer's handover code; refunds and manual completions stay with admins.</p>
            </div>
            <div className="flex gap-3 mt-6">
              <button type="submit" disabled={busy} className="h-11 px-6 rounded-full bg-gold hover:bg-gold-hover text-white font-bold text-sm disabled:opacity-50 transition-colors" data-testid="create-user-submit">
                {busy ? "Creating…" : "Create account"}
              </button>
              <button type="button" onClick={() => setCreateOpen(false)} className="h-11 px-6 rounded-full border border-slate-200 font-semibold text-sm" data-testid="create-user-cancel">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function ChangePasswordCard() {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.next !== form.confirm) {
      toast.error("New passwords don't match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      toast.success("Password changed — use it next time you sign in");
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "mt-1 w-full h-11 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:ring-2 focus:ring-gold";
  return (
    <form onSubmit={submit} className="bg-white rounded-3xl border border-slate-200/80 p-8 max-w-xl mt-6" data-testid="change-password-card">
      <h3 className="font-display font-bold text-navy text-lg mb-1">Change your password</h3>
      <p className="text-xs text-slate-400 mb-6">Changes take effect immediately and survive restarts.</p>
      <div className="space-y-4">
        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Current password</span>
          <PasswordInput required value={form.current} onChange={(e) => setForm({ ...form, current: e.target.value })} className={inputCls} data-testid="pw-current" />
        </label>
        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">New password (min 6 characters)</span>
          <PasswordInput required minLength={6} value={form.next} onChange={(e) => setForm({ ...form, next: e.target.value })} className={inputCls} data-testid="pw-new" />
        </label>
        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Confirm new password</span>
          <PasswordInput required minLength={6} value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className={inputCls} data-testid="pw-confirm" />
        </label>
      </div>
      <button disabled={busy} className="mt-6 h-11 px-8 rounded-full bg-navy text-white font-bold text-sm disabled:opacity-50 transition-colors" data-testid="pw-change-submit">
        {busy ? "Saving…" : "Change password"}
      </button>
    </form>
  );
}
